import { useCallback, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

/**
 * Drop-in replacement for useState that mirrors the value into sessionStorage.
 *
 * Used by report grids so filters, sorting and the current page survive a
 * drill-down into a detail view and the subsequent navigation back, which
 * unmounts and remounts the grid.
 *
 * Pass a null storageKey to opt out of persistence.
 */
export function usePersistedState<T>(
  storageKey: string | null,
  defaultValue: T,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    if (!storageKey) return defaultValue;
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored !== null) return JSON.parse(stored) as T;
    } catch (error) {
      console.error(`Failed to read ${storageKey} from sessionStorage:`, error);
    }
    return defaultValue;
  });

  const valueRef = useRef(value);
  valueRef.current = value;

  const setPersistedValue = useCallback<Dispatch<SetStateAction<T>>>(
    (next) => {
      const resolved = typeof next === 'function'
        ? (next as (prev: T) => T)(valueRef.current)
        : next;
      valueRef.current = resolved;
      setValue(resolved);
      if (!storageKey) return;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(resolved));
      } catch (error) {
        console.error(`Failed to write ${storageKey} to sessionStorage:`, error);
      }
    },
    [storageKey],
  );

  return [value, setPersistedValue];
}

/** Removes every persisted value stored under the given keys. */
export function clearPersistedState(storageKeys: string[]): void {
  try {
    storageKeys.forEach(key => sessionStorage.removeItem(key));
  } catch (error) {
    console.error('Failed to clear persisted state from sessionStorage:', error);
  }
}
